import {Injectable} from '@angular/core';
import {IDemandePermisVisite} from '../models/demande-permis-visite.model';
import {StatutInfo} from '../models/enumerations/statut-info.model';
import {matriceNavigationEtapesSansPieceMap} from '../models/enumerations/matriceNavigationEtapesSansPieceMap.model';
import {etapeDemandeMap} from '../models/enumerations/etape-demande.model';
import {matriceNavigationEtapesAvecPieceMap} from '../models/enumerations/matriceNavigationEtapesAvecPiecesMap.model';
import {DemandePermisVisiteService} from './demande-permis-visite.service';
import {IPieceJustificative} from '../models/piece-justificative.model';
import {PjFile} from '../models/piece-jointe-demande.model';
import {TypePj} from '../models/enumerations/type-pj.model';

@Injectable({providedIn: 'root'})
export class NavigationTunnelService {

    demande: IDemandePermisVisite | undefined;
    listeStatutsEnRejet: string[] = [];
    listePjsRejetees: IPieceJustificative[] = [];
    listeEtapesAVisiter: number[] = [];
    listeEtapesVisitees: number[] | undefined;
    listePjsTraitees: TypePj[] = [];

    listePjsASupprimerCompletionDemande: PjFile[] = [];
    listePjsAEnregistrerCompletionDemande: PjFile[] = [];

    matricePj = matriceNavigationEtapesAvecPieceMap;

    retourInfosDetenu = false;
    retourInfosVisiteur = false;
    retourInfosComplementaires = false;
    retourInfosMajeur = false;
    retourPagePieceIdentite = false;
    retourPagePieceLien = false;
    retourPagePieceMineur = false;
    retourPagePieceJointe = false;
    retourPagePiecePhoto = false;
    retourPagePieceJustificatifDomicile = false;
    retourPagePieceComplementaire1 = false;
    retourPagePieceComplementaire2 = false;
    retourPagePieceComplementaire3 = false;

    constructor(
        private demandePermisVisiteService: DemandePermisVisiteService,
    ) {
        // AJout de compo si besoin
    }

    /**
     * Permet de construire la liste des statuts en rejet pour une demande donnée
     * @param demande La demande concernée
     * @return La liste des étapes à visiter
     */
    constructionListeStatutsEnRejet(demande: IDemandePermisVisite) {
        this.demande = demande;

        this.demandePermisVisiteService.setDemandeIncompleteEnCoursDeModification(this.demande);

        this.listeEtapesVisitees = [];

        // Pour chaque élément de la matrice de navigation
        matriceNavigationEtapesSansPieceMap.forEach((listeDonneesEtape: string[], numeroEtape: number) => {
            // On cherche les string de cette liste dans les attributs de la demande
            this.rechercheStatutsRejetesInformations(listeDonneesEtape, numeroEtape);
        });

        // On ajoute à la liste des étapes à visiter les étapes qui concernent les PJs au statut REJETER
        this.rechercheStatutsRejetesPJ();

        // Ajout de l'étape permettant de saisir un commentaire
        this.listeEtapesAVisiter.push(17);

        const listeEtapesAVisiterTriees = this.listeEtapesAVisiter.sort((n1, n2) => n1 - n2);

        return this.listeEtapesAVisiter = listeEtapesAVisiterTriees;
    }

    /**
     * Permet pour chaque étape de rechercher l'ensemble des statuts à REJETER
     * pour les infomations non PJ
     * @param listeDonneesEtape La liste des données présentes pour une étape donnée
     * @param numeroEtape La numéro de l'étape
     * @private
     */
    private rechercheStatutsRejetesInformations(listeDonneesEtape: string[], numeroEtape: number) {
        // Pour chaque path de la liste de l'étape
        this.demande && listeDonneesEtape.forEach(chemin => {
            const tableauChemin = chemin.split('.');
            let entiteEtudieeDynamique: any = this.demande!;
            tableauChemin.forEach(element => {
                const tmp: any = entiteEtudieeDynamique[element];
                if (tmp) {
                    entiteEtudieeDynamique = tmp;
                }
            });
            // Si le statut est à REJETER
            if (entiteEtudieeDynamique === StatutInfo.REJETER) {
                // On ajoute le path dans la liste des statuts en rejet
                this.listeStatutsEnRejet.push(tableauChemin[tableauChemin.length - 1]);
                // On ajoute le numéro d'étape dans la liste qui nous permettra de naviguer
                this.listeEtapesAVisiter.push(numeroEtape);
            }
        });
    }

    /**
     * Permet pour chaque étape de rechercher l'ensemble des statuts à REJETER
     * pour les PJ
     * @private
     */
    private rechercheStatutsRejetesPJ() {
        (this.demande?.pieceJustificatives || []).forEach(piece => {
            if (piece?.statutPieceJustificative?.statut === StatutInfo.REJETER) {
                const tmp = Array.from(this.matricePj)
                    .find(e => e[1] === piece.type);

                if (tmp) {
                    const numeroEtapePjAVisiter: number = tmp[0];
                    this.listeEtapesAVisiter.push(numeroEtapePjAVisiter);
                    // On ajoute la piece rejetée dans la liste qui sera consultée lors de la reprise
                    this.listePjsRejetees.push(piece);
                }
            }
        });
        (this.demande?.pieceJustificativeComplementaires || []).forEach((piece, index) => {
            // pas de statut = demande d'une nouvelle pjComplémentaire par l'agent
            // NB: on traite les nouvelles demandes de PJcompl comme des refus dans le tunnel
            if (!piece.statutPieceJustificativeComplementaires
                || piece.statutPieceJustificativeComplementaires.statut === StatutInfo.REJETER) {
                this.listeEtapesAVisiter.push(14 + index);
                // On ajoute la piece rejetée dans la liste qui sera consultée lors de la reprise
                this.listePjsRejetees.push(piece);
            }
        });
    }

    /**
     * Permet de supprimer une étape de la liste des étapes à visiter
     * une fois que celle-ci a été visitée
     * @param numeroEtapeVisitee Le numéro d'étape à supprimer
     * @private
     */
    private supprimmerEtapeVisitee(numeroEtapeVisitee: number) {
        this.listeEtapesAVisiter = this.listeEtapesAVisiter.filter(numeroEtape => numeroEtape !== numeroEtapeVisitee);
    }

    /**
     * Permet de retourner le path de la prochaine étape à visiter
     * @private
     */
    private determinerPathEtapeCibleTunnelDemandeACompleter(numeroEtapeCible: number) {
        return '/tunneldemandepermisvisite/' + etapeDemandeMap[numeroEtapeCible];
    }

    /**
     * Permet de mettre à jour la liste des étapes à visiter
     * et retourne le path de l'étape suivante
     * @param numeroEtapeVisitee Le numéro d'étape visitée
     */
    navigationEtapeSuivanteDemandeACompleter(numeroEtapeVisitee: number) {
        !this.listeEtapesVisitees && (this.listeEtapesVisitees = []);
        // On supprime le numéro d'étape visitée pour éviter les doublons dans le cas d'un retour arrière

        // On garde en mémoire les étapes visitées en cas de retour arrière
        this.listeEtapesVisitees.push(numeroEtapeVisitee);
        // On supprime l'étape visitée de la liste des étapes à visiter
        this.supprimmerEtapeVisitee(numeroEtapeVisitee);
        // On détermine le path de l'étape suivante à modifier
        return this.determinerPathEtapeCibleTunnelDemandeACompleter(this.listeEtapesAVisiter[0]);
    }

    /**
     * Permet de retourner le path de l'étape précédemment visitée
     */
    navigationEtapePrecedenteDemandeACompleter() {
        !this.listeEtapesVisitees && (this.listeEtapesVisitees = []);
        const pathEtapePrecedente = this.determinerPathEtapeCibleTunnelDemandeACompleter(this.listeEtapesVisitees[this.listeEtapesVisitees.length - 1]);
        // On ajoute l'étape qui redevient une étape à visiter
        this.listeEtapesAVisiter.push(this.listeEtapesVisitees[this.listeEtapesVisitees.length - 1]);
        // On remet la liste des étapes à visiter dans l'ordre croissant des étapes
        this.listeEtapesAVisiter = this.listeEtapesAVisiter.sort((n1, n2) => n1 - n2);
        // On supprime l'étape de la liste des étapes visitées
        this.listeEtapesVisitees = this.listeEtapesVisitees.filter(numeroEtape => numeroEtape !== this.listeEtapesVisitees![this.listeEtapesVisitees!.length - 1]);

        return pathEtapePrecedente;
    }

    /**
     * Méthode permettant de déterminer si l'utilisateur
     * est sur la première page nécessitant une modification
     */
    determineSiPremierePageAvecModifDemandeACompleter() {
        if (this.listeEtapesVisitees) {
            return !this.listeEtapesVisitees.length;
        }
        return false;
    }

    /**
     * Méthode permettant de réinitialiser toutes les informations
     * stockées en rapport à la demande incomplète
     */
    reinitialisationDonneesDemandeIncomplete() {
        this.listeStatutsEnRejet = [];
        this.listeEtapesAVisiter = [];
        this.listeEtapesVisitees = [];
        this.listePjsTraitees = [];
        this.listePjsRejetees = [];
        this.listePjsAEnregistrerCompletionDemande = [];
        this.listePjsASupprimerCompletionDemande = [];
        this.demandePermisVisiteService.setDemandeIncomplete(false);
        this.demande = undefined;
        this.retourInfosDetenu = false;
        this.retourInfosVisiteur = false;
        this.retourInfosComplementaires = false;
        this.retourInfosMajeur = false;
        this.retourPagePieceIdentite = false;
        this.retourPagePieceMineur = false;
        this.retourPagePieceLien = false;
        this.retourPagePieceJointe = false;
        this.retourPagePiecePhoto = false;
        this.retourPagePieceJustificatifDomicile = false;
    }
}
